import { buildWordDoc, downloadWord, textToHtml } from '../mca/documentGenerators';
import { fmt } from './dprFields';

function table(headers, rows) {
  return `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
}

// Builds the full report as one HTML document and triggers a .doc download —
// reuses the same MIME-trick export used by the MCA module (documentGenerators.js),
// no new dependency required.
export function exportCaseWord(caseData, schedules) {
  const isCma = caseData.case_mode === 'cma_only';
  const years = schedules.profitLoss.map(p => typeof p.year === 'number' ? `Yr ${p.year}` : p.year);
  const bp = caseData.business_profile || {};
  const cp = caseData.company_profile || {};
  let body = `<h1>${caseData.project_title || 'Project Report'}</h1><h2>${caseData.applicant_name || ''} — CMA Data</h2>`;

  if (isCma) {
    body += `<p>
      ${cp.businessFormation ? `<b>Constitution:</b> ${cp.businessFormation} &nbsp; ` : ''}
      ${cp.bankName ? `<b>Bank:</b> ${cp.bankName}${cp.branchName ? ' — ' + cp.branchName : ''} &nbsp; ` : ''}
      ${cp.existingCCLimit ? `<b>Existing Limit:</b> ${fmt(cp.existingCCLimit)} &nbsp; ` : ''}
      ${cp.proposedCCLimit ? `<b>Proposed Limit:</b> ${fmt(cp.proposedCCLimit)}` : ''}
    </p>`;
  } else {
    body += `<p>
      ${bp.businessFormation ? `<b>Constitution:</b> ${bp.businessFormation} &nbsp; ` : ''}
      ${bp.sector ? `<b>Sector:</b> ${bp.sector}${bp.subSector ? ' / ' + bp.subSector : ''} &nbsp; ` : ''}
      ${bp.location ? `<b>Location:</b> ${bp.location} &nbsp; ` : ''}
      ${bp.promoterName ? `<b>Key Promoter:</b> ${bp.promoterName}${bp.promoterQualification ? ' (' + bp.promoterQualification + ')' : ''}` : ''}
    </p>`;
  }
  body += `<p>${isCma ? '' : `<b>Cost of Project:</b> ${fmt(schedules.costOfProjectTotal)} &nbsp; `}<b>Average DSCR:</b> ${fmt(schedules.dscr.averageDSCR)}x</p>`;

  body += `<h2>Profit &amp; Loss</h2>` + table(['Particulars', ...years], [
    ['Gross Sales', ...schedules.profitLoss.map(p => fmt(p.grossSale))],
    ['Cost of Sales', ...schedules.profitLoss.map(p => fmt(p.costOfSales))],
    ['Gross Profit', ...schedules.profitLoss.map(p => fmt(p.grossProfit))],
    ['Interest (Term Loan)', ...schedules.profitLoss.map(p => fmt(p.tlInterest))],
    ['Interest (Working Capital)', ...schedules.profitLoss.map(p => fmt(p.wcInterest))],
    ['Net Profit Before Tax', ...schedules.profitLoss.map(p => fmt(p.npbt))],
    ['Tax', ...schedules.profitLoss.map(p => fmt(p.tax))],
    ['Net Profit After Tax', ...schedules.profitLoss.map(p => fmt(p.npat))],
  ]);

  body += `<h2>Balance Sheet</h2>` + table(['Particulars', ...years], [
    ['Capital', ...schedules.balanceSheet.map(b => fmt(b.capital))],
    ['Reserves', ...schedules.balanceSheet.map(b => fmt(b.reserves))],
    ['Term Loan', ...schedules.balanceSheet.map(b => fmt(b.termLoan))],
    ['WC Borrowing', ...schedules.balanceSheet.map(b => fmt(b.wcBorrowing))],
    ['Creditors', ...schedules.balanceSheet.map(b => fmt(b.creditors))],
    ['Total Liabilities', ...schedules.balanceSheet.map(b => fmt(b.totalLiabilities))],
    ['Freehold Land', ...schedules.balanceSheet.map(b => fmt(b.freeholdLand))],
    ['Net Fixed Assets', ...schedules.balanceSheet.map(b => fmt(b.netBlock))],
    ['Cash & Bank', ...schedules.balanceSheet.map(b => fmt(b.cash))],
    ['Debtors', ...schedules.balanceSheet.map(b => fmt(b.debtors))],
    ['Stock', ...schedules.balanceSheet.map(b => fmt(b.stock))],
    ['Total Assets', ...schedules.balanceSheet.map(b => fmt(b.totalAssets))],
  ]);

  if (schedules.cashFlow) {
    body += `<h2>Cash Flow Statement</h2>` + table(['Particulars', ...years], [
      ['Opening Cash', ...schedules.cashFlow.map(c => fmt(c.opening))],
      ['Sources', ...schedules.cashFlow.map(c => fmt(c.sources))],
      ['Uses', ...schedules.cashFlow.map(c => fmt(c.uses))],
      ['Closing Cash', ...schedules.cashFlow.map(c => fmt(c.closing))],
    ]);
  }

  body += `<h2>DSCR</h2>` + table(['Particulars', ...years], [
    ['Net Operating Income', ...schedules.dscr.rows.map(r => fmt(r.netOperatingIncome))],
    ['Debt Service', ...schedules.dscr.rows.map(r => fmt(r.debtService))],
    ['DSCR', ...schedules.dscr.rows.map(r => fmt(r.dscr))],
  ]);
  body += `<p><b>Average DSCR: ${fmt(schedules.dscr.averageDSCR)}</b></p>`;

  body += `<h2>Financial Ratios</h2>` + table(['Particulars', ...years], [
    ['Gross Profit Ratio %', ...schedules.ratios.map(r => fmt(r.grossProfitRatio))],
    ['PAT / Turnover %', ...schedules.ratios.map(r => fmt(r.patToTurnover))],
    ['PAT / TNW %', ...schedules.ratios.map(r => fmt(r.patToTNW))],
    ['ROCE %', ...schedules.ratios.map(r => fmt(r.roce))],
    ['Interest Coverage', ...schedules.ratios.map(r => fmt(r.interestCoverage))],
    ['Current Ratio', ...schedules.ratios.map(r => fmt(r.currentRatio))],
    ['TOL / TNW', ...schedules.ratios.map(r => fmt(r.tolToTNW))],
    ['Debt-Equity', ...schedules.ratios.map(r => fmt(r.debtEquity))],
  ]);

  if (schedules.bep) {
    body += `<h2>Break-Even Point &amp; Bankability</h2>` + table(['Particulars', ...years], [
      ['Contribution', ...schedules.bep.map(b => fmt(b.contribution))],
      ['Fixed Expenses', ...schedules.bep.map(b => fmt(b.fixedExp))],
      ['BEP Sales Value', ...schedules.bep.map(b => fmt(b.bepSalesValue))],
      ['BEP %', ...schedules.bep.map(b => fmt(b.bepPct))],
      ['EBITDA', ...schedules.bep.map(b => fmt(b.ebitda))],
      ['EBITDA Margin %', ...schedules.bep.map(b => fmt(b.ebitdaMarginPct))],
      ['ISCR', ...schedules.bep.map(b => fmt(b.iscr))],
    ]);
  }
  if (schedules.bankability) {
    body += `<p><b>Project IRR (@ ${schedules.bankability.discountRatePct}% discount):</b> ${schedules.bankability.irrPct === null ? 'n/a' : fmt(schedules.bankability.irrPct) + '%'} &nbsp; <b>NPV:</b> ${fmt(schedules.bankability.npv)}</p>`;
  }

  body += `<h2>Working Capital</h2>` + table(['Particulars', ...years], [
    ['Method', ...schedules.workingCapital.byYear.map(w => w.method)],
    ['Debtors', ...schedules.workingCapital.byYear.map(w => fmt(w.debtors))],
    ['Creditors', ...schedules.workingCapital.byYear.map(w => fmt(w.creditors))],
    ['Stock', ...schedules.workingCapital.byYear.map(w => fmt(w.stock))],
    ['Bank Finance / MPBF', ...schedules.workingCapital.byYear.map(w => fmt(w.mpbf ?? w.bankFinance ?? w.totalWC))],
  ]);

  body += `<h2>Term Loan Repayment (Annual)</h2>` + table(['Particulars', ...years], [
    ['Opening Balance', ...schedules.repayment.byYear.map(r => fmt(r.openingBalance))],
    ['Interest', ...schedules.repayment.byYear.map(r => fmt(r.interest))],
    ['Installment', ...schedules.repayment.byYear.map(r => fmt(r.installment))],
    ['Closing Balance', ...schedules.repayment.byYear.map(r => fmt(r.closingBalance))],
  ]);

  body += `<h2>RBI CMA Form I — Particulars of Existing / Proposed Limits</h2>` + table(['Particulars', 'Existing', 'Proposed'],
    schedules.forms.formI.facilities.map(f => [f.particulars, fmt(f.existing), fmt(f.proposed)])
      .concat([['Total', fmt(schedules.forms.formI.totalExisting), fmt(schedules.forms.formI.totalProposed)]])
  );

  body += `<h2>RBI CMA Form II — Operating Statement</h2>` + table(['Particulars', ...years], [
    ['Net Sales', ...schedules.forms.formII.map(f => fmt(f.netSales))],
    ['Cost of Sales', ...schedules.forms.formII.map(f => fmt(f.costOfSales))],
    ['Operating Profit before Interest', ...schedules.forms.formII.map(f => fmt(f.operatingProfitBeforeInterest))],
    ['Interest', ...schedules.forms.formII.map(f => fmt(f.interest))],
    ['Operating Profit after Interest', ...schedules.forms.formII.map(f => fmt(f.operatingProfitAfterInterest))],
    ['Profit Before Tax', ...schedules.forms.formII.map(f => fmt(f.profitBeforeTax))],
    ['Tax', ...schedules.forms.formII.map(f => fmt(f.tax))],
    ['Net Profit', ...schedules.forms.formII.map(f => fmt(f.netProfit))],
  ]);

  body += `<h2>RBI CMA Form III — Analysis of Balance Sheet</h2>` + table(['Particulars', ...years], [
    ['Total Current Liabilities', ...schedules.forms.formIII.map(f => fmt(f.totalCurrentLiabilities))],
    ['Total Term Liabilities', ...schedules.forms.formIII.map(f => fmt(f.totalTermLiabilities))],
    ['Total Outside Liabilities', ...schedules.forms.formIII.map(f => fmt(f.totalOutsideLiabilities))],
    ['Net Worth', ...schedules.forms.formIII.map(f => fmt(f.netWorth))],
    ['Total Current Assets', ...schedules.forms.formIII.map(f => fmt(f.totalCurrentAssets))],
    ['Net Block', ...schedules.forms.formIII.map(f => fmt(f.netBlock))],
    ['Total Assets', ...schedules.forms.formIII.map(f => fmt(f.totalAssets))],
    ['Tangible Net Worth', ...schedules.forms.formIII.map(f => fmt(f.tangibleNetWorth))],
    ['Net Working Capital', ...schedules.forms.formIII.map(f => fmt(f.netWorkingCapital))],
    ['Current Ratio', ...schedules.forms.formIII.map(f => fmt(f.currentRatio))],
    ['TOL / TNW', ...schedules.forms.formIII.map(f => fmt(f.tolToTNW))],
    ['Term Liab / TNW', ...schedules.forms.formIII.map(f => fmt(f.termLiabToTNW))],
  ]);

  body += `<h2>RBI CMA Form IV — Comparative Statement of Current Assets &amp; Current Liabilities</h2>` + table(['Particulars', ...years], [
    ['Raw Material & Stock', ...schedules.forms.formIV.map(f => fmt(f.rawMaterialAndStock))],
    ['Receivables', ...schedules.forms.formIV.map(f => fmt(f.receivables))],
    ['Other Current Assets', ...schedules.forms.formIV.map(f => fmt(f.otherCurrentAssets))],
    ['Total Current Assets', ...schedules.forms.formIV.map(f => fmt(f.totalCurrentAssets))],
    ['Creditors for Purchases', ...schedules.forms.formIV.map(f => fmt(f.creditorsForPurchases))],
    ['Other Current Liabilities', ...schedules.forms.formIV.map(f => fmt(f.otherCurrentLiabilities))],
    ['Total Current Liabilities', ...schedules.forms.formIV.map(f => fmt(f.totalCurrentLiabilities))],
  ]);

  body += `<h2>RBI CMA Form V — Computation of MPBF (${schedules.forms.formV.method})</h2>` + table(['Particulars', ...years],
    schedules.forms.formV.method === 'traditional' ? [
      ['Paid Stock', ...schedules.forms.formV.byYear.map(w => fmt(w.paidStock))],
      ['Stock Bank Finance', ...schedules.forms.formV.byYear.map(w => fmt(w.stockBankFinance))],
      ['Debtor Bank Finance', ...schedules.forms.formV.byYear.map(w => fmt(w.debtorBankFinance))],
      ['MPBF', ...schedules.forms.formV.byYear.map(w => fmt(w.mpbf))],
    ] : schedules.forms.formV.method === 'turnover' ? [
      ['Total Working Capital', ...schedules.forms.formV.byYear.map(w => fmt(w.totalWC))],
      ['Promoter Margin', ...schedules.forms.formV.byYear.map(w => fmt(w.margin))],
      ['Bank Finance', ...schedules.forms.formV.byYear.map(w => fmt(w.bankFinance))],
    ] : [
      ['Current Assets', ...schedules.forms.formV.byYear.map(w => fmt(w.currentAssets))],
      ['Other Current Liabilities', ...schedules.forms.formV.byYear.map(w => fmt(w.otherCurrentLiabilities))],
      ['MPBF', ...schedules.forms.formV.byYear.map(w => fmt(w.mpbf))],
      ['Promoter Margin', ...schedules.forms.formV.byYear.map(w => fmt(w.promoterMargin))],
    ]
  );

  body += `<h2>RBI CMA Form VI — Funds Flow Statement</h2>` + table(['Particulars', ...years], [
    ['Sources', ...schedules.forms.formVI.map(f => fmt(f.sources))],
    ['Uses', ...schedules.forms.formVI.map(f => fmt(f.uses))],
    ['Long Term Surplus / Deficit', ...schedules.forms.formVI.map(f => fmt(f.longTermSurplus))],
    ['Increase in Current Assets', ...schedules.forms.formVI.map(f => fmt(f.caIncrease))],
    ['Increase in Current Liabilities', ...schedules.forms.formVI.map(f => fmt(f.clIncrease))],
    ['Net Surplus / Deficit', ...schedules.forms.formVI.map(f => fmt(f.netSurplus))],
    ['Increase in Bank Borrowing', ...schedules.forms.formVI.map(f => fmt(f.bankBorrowingIncrease))],
  ]);

  body += `<h2>RBI CMA Form VII — Ratio Analysis Dashboard</h2>` + table(['Particulars', ...years], [
    ['Gross Profit Ratio %', ...schedules.forms.formVII.rows.map(r => fmt(r.grossProfitRatio))],
    ['PAT / Turnover %', ...schedules.forms.formVII.rows.map(r => fmt(r.patToTurnover))],
    ['Current Ratio', ...schedules.forms.formVII.rows.map(r => fmt(r.currentRatio))],
    ['TOL / TNW', ...schedules.forms.formVII.rows.map(r => fmt(r.tolToTNW))],
    ['DSCR', ...schedules.forms.formVII.rows.map(r => fmt(r.dscr))],
  ]);

  downloadWord(`${caseData.project_title || 'DPR'}_CMA_Report`, body);
}

// Full bank-ready DPR: narrative chapters (if drafted) followed by the complete
// CMA numeric annexure — matches the "narrative front + CMA numeric back"
// structure documented from real bank-submitted DPRs.
export function exportFullDPR(caseData, schedules) {
  const n = caseData.narrative_sections;
  if (!n || !Object.keys(n).length) return exportCaseWord(caseData, schedules);

  const bp = caseData.business_profile || {};
  let body = `<h1>${caseData.project_title || 'Project Report'}</h1><h2>Detailed Project Report for ${caseData.applicant_name || ''}</h2>`;

  body += `<h2>1. Project Brief</h2>${textToHtml(n.projectBrief)}`;
  body += `<h2>2. Industry Introduction</h2>${textToHtml(n.industryIntroduction)}`;
  body += `<h2>3. About Us</h2>${textToHtml(n.aboutUs)}`;
  body += `<h2>4. Manufacturing / Service Process</h2>${textToHtml(n.manufacturingProcess)}`;

  if (n.pestel) {
    body += `<h2>5. PESTEL Analysis</h2>`;
    body += ['political', 'economic', 'social', 'technological', 'ecological', 'legal']
      .map(k => `<p><b>${k.charAt(0).toUpperCase() + k.slice(1)}:</b> ${n.pestel[k] || ''}</p>`).join('');
  }
  if (n.swot) {
    body += `<h2>6. SWOT Analysis</h2>`;
    body += ['strengths', 'weaknesses', 'opportunities', 'threats'].map(k =>
      `<p><b>${k.charAt(0).toUpperCase() + k.slice(1)}:</b></p><ul>${(n.swot[k] || []).map(i => `<li>${i}</li>`).join('')}</ul>`
    ).join('');
  }
  if (n.implementationSchedule?.length) {
    body += `<h2>7. Implementation Schedule</h2>` + table(['Activity', 'Target Period'], n.implementationSchedule.map(r => [r.activity, r.targetPeriod]));
  }
  body += `<h2>8. Resource Requirements</h2>${textToHtml(n.resourceRequirements)}`;
  if (n.assumptions?.length) {
    body += `<h2>9. Assumptions</h2><ol>${n.assumptions.map(a => `<li>${a}</li>`).join('')}</ol>`;
  }
  body += `<h2>10. Conclusion</h2>${textToHtml(n.conclusion)}`;

  if (n.financialAppraisal) {
    const fa = n.financialAppraisal;
    body += `<h2>11. Independent Financial Appraisal &amp; Risk Assessment</h2>`;
    body += `<h3>Capital Structure &amp; Funding</h3>${textToHtml(fa.capitalStructureFunding)}`;
    body += `<h3>Debt-Servicing Capacity</h3>${textToHtml(fa.debtServicingCapacity)}`;
    body += `<h3>Profitability &amp; Returns</h3>${textToHtml(fa.profitabilityReturns)}`;
    body += `<h3>Liquidity &amp; Working Capital</h3>${textToHtml(fa.liquidityWorkingCapital)}`;
    body += `<h3>Break-Even &amp; Resilience</h3>${textToHtml(fa.breakEvenResilience)}`;
    if (fa.riskObservations?.length) body += `<h3>Risk Observations</h3><ul>${fa.riskObservations.map(r => `<li>${r}</li>`).join('')}</ul>`;
    body += `<h3>Overall Assessment</h3>${textToHtml(fa.overallAssessment)}`;
    if (n.appraisalDisclaimer) body += `<p><i>${n.appraisalDisclaimer}</i></p>`;
  }

  body += `<h2>Disclaimer</h2>${textToHtml(n.disclaimer)}`;
  body += `<h2>Promoter Details</h2><p>${bp.promoterName || ''}${bp.promoterQualification ? ' — ' + bp.promoterQualification : ''}${bp.promoterExperience ? ` (${bp.promoterExperience} yrs experience)` : ''}</p>`;

  downloadWord(`${caseData.project_title || 'DPR'}_Full_Report`, body);

  // CMA numeric annexure is a separate document, matching how these are actually
  // filed with a bank (narrative DPR + CMA data as two linked deliverables).
  exportCaseWord(caseData, schedules);
}

export { buildWordDoc };
